import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, DollarSign, Shield, CheckCircle, Upload, AlertCircle } from 'lucide-react';
import NearULogo from '../components/NearULogo';

const IncomeVerificationPage = () => {
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('verified'); // verified, pending, not_verified

  const incomeTiers = [
    {
      name: 'Gold',
      range: '$5M - $10M',
      color: '#D4AF37',
      fillColor: '#D4AF37',
      type: 'coin',
      description: 'Verified annual income between $5,000,000 and $10,000,000'
    },
    {
      name: 'Diamond',
      range: '$11M+',
      color: '#4169E1',
      fillColor: '#4169E1',
      type: 'diamond',
      description: 'Verified annual income above $11,000,000'
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0D0D0D 0%, #2A1B0E 100%)' }}>
      <div className="page-header">
        <button
          onClick={() => navigate('/profile')}
          style={{
            background: 'none',
            border: 'none',
            color: '#D4AF37',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
            fontSize: '14px',
            padding: '8px 0'
          }}
        >
          <ChevronLeft size={20} />
          Back to Profile
        </button>
        <div style={{ marginBottom: '12px' }}>
          <NearULogo size="small" showText={true} />
        </div>
        <h1 className="page-title" style={{ fontFamily: 'Cinzel, Playfair Display, serif' }}>
          INCOME VERIFICATION
        </h1>
        <p className="page-subtitle" style={{ letterSpacing: '1px' }}>Elite Members Only</p>
      </div>

      <div style={{ padding: '24px' }}>
        {/* Elite Badge */}
        <div style={{
          textAlign: 'center',
          padding: '24px',
          background: 'linear-gradient(145deg, rgba(212, 175, 55, 0.15), rgba(212, 175, 55, 0.05))',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '2px solid rgba(212, 175, 55, 0.4)',
          marginBottom: '32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 16px',
            background: 'linear-gradient(145deg, #D4AF37, #B8941F)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(212, 175, 55, 0.4)',
            border: '3px solid rgba(244, 231, 195, 0.5)'
          }}>
            <DollarSign size={40} style={{ color: '#0D0D0D' }} strokeWidth={2.5} />
          </div>
          <h2 style={{
            fontSize: '24px',
            fontFamily: 'Cinzel, serif',
            color: '#D4AF37',
            marginBottom: '8px'
          }}>
            Income Verification
          </h2>
          <p style={{ color: '#F4E7C3', fontSize: '14px', lineHeight: '1.6' }}>
            Showcase your success with our exclusive diamond tier badges
          </p>
        </div>

        {/* Current Status */}
        <div className="glass-panel" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            {verificationStatus === 'verified' ? (
              <CheckCircle size={24} style={{ color: '#50C878' }} />
            ) : verificationStatus === 'pending' ? (
              <AlertCircle size={24} style={{ color: '#D4AF37' }} />
            ) : (
              <Shield size={24} style={{ color: '#F4E7C3' }} />
            )}
            <h3 style={{ fontSize: '18px', color: '#D4AF37', fontFamily: 'Cinzel, serif' }}>
              Verification Status
            </h3>
          </div>
          <p style={{ color: '#F4E7C3', fontSize: '15px', marginBottom: '12px' }}>
            {verificationStatus === 'verified' && 'Your income has been verified. Your diamond badge is active.'}
            {verificationStatus === 'pending' && 'Your verification is under review. We\'ll notify you once complete.'}
            {verificationStatus === 'not_verified' && 'Complete income verification to earn your diamond badge.'}
          </p>
          {verificationStatus === 'verified' && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              background: 'linear-gradient(135deg, rgba(192, 192, 192, 0.2), rgba(160, 160, 160, 0.1))',
              borderRadius: '12px',
              color: '#C0C0C0',
              fontWeight: 'bold',
              fontSize: '14px',
              border: '2px solid rgba(192, 192, 192, 0.6)',
              boxShadow: '0 4px 16px rgba(192, 192, 192, 0.4)',
              backdropFilter: 'blur(10px)'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="silverCoin" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#C0C0C0', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#A0A0A0', stopOpacity: 1 }} />
                  </linearGradient>
                  <filter id="silverGlow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <circle 
                  cx="12" 
                  cy="12" 
                  r="9" 
                  fill="url(#silverCoin)"
                  stroke="#C0C0C0"
                  strokeWidth="2"
                  filter="url(#silverGlow)"
                />
                <circle 
                  cx="12" 
                  cy="12" 
                  r="6" 
                  fill="none"
                  stroke="#C0C0C0"
                  strokeWidth="1"
                  opacity="0.5"
                />
                <text
                  x="12"
                  y="15"
                  textAnchor="middle"
                  fill="#0D0D0D"
                  fontSize="8"
                  fontWeight="bold"
                >
                  S
                </text>
              </svg>
              Silver
            </div>
          )}
        </div>

        {/* Diamond Tiers */}
        <h3 style={{
          fontSize: '20px',
          color: '#D4AF37',
          fontFamily: 'Cinzel, serif',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          Income Verification Tiers
        </h3>

        <div style={{ display: 'grid', gap: '16px', marginBottom: '32px' }}>
          {incomeTiers.map((tier, idx) => (
            <div
              key={idx}
              className="luxury-card"
              style={{
                background: 'linear-gradient(145deg, rgba(28, 28, 28, 0.95), rgba(13, 13, 13, 0.98))',
                padding: '20px',
                borderRadius: '16px',
                border: `2px solid ${tier.color}40`,
                boxShadow: `0 8px 24px rgba(0, 0, 0, 0.6), 0 0 20px ${tier.color}20`
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id={`tierGrad-${idx}`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{ stopColor: tier.fillColor, stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: tier.fillColor, stopOpacity: 0.8 }} />
                      </linearGradient>
                      <filter id={`tierGlow-${idx}`}>
                        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>
                    {tier.type === 'coin' ? (
                      <>
                        <circle 
                          cx="12" 
                          cy="12" 
                          r="9" 
                          fill={`url(#tierGrad-${idx})`}
                          stroke={tier.color}
                          strokeWidth="2"
                          filter={`url(#tierGlow-${idx})`}
                        />
                        <circle 
                          cx="12" 
                          cy="12" 
                          r="6" 
                          fill="none"
                          stroke={tier.color}
                          strokeWidth="1"
                          opacity="0.5"
                        />
                        <text
                          x="12"
                          y="15"
                          textAnchor="middle"
                          fill="#0D0D0D"
                          fontSize="8"
                          fontWeight="bold"
                        >
                          {tier.name.charAt(0)}
                        </text>
                      </>
                    ) : (
                      <>
                        {/* Realistic Diamond Stone Shape */}
                        {/* Top facets (table and crown) */}
                        <path 
                          d="M12 4 L8 9 L12 10 L16 9 Z" 
                          fill={tier.color}
                          opacity="0.9"
                          stroke={tier.color}
                          strokeWidth="0.5"
                        />
                        {/* Upper girdle facets */}
                        <path 
                          d="M8 9 L5 11 L12 11 Z" 
                          fill={`url(#tierGrad-${idx})`}
                          stroke={tier.color}
                          strokeWidth="0.5"
                          opacity="0.8"
                        />
                        <path 
                          d="M16 9 L19 11 L12 11 Z" 
                          fill={`url(#tierGrad-${idx})`}
                          stroke={tier.color}
                          strokeWidth="0.5"
                          opacity="0.8"
                        />
                        {/* Lower girdle and pavilion */}
                        <path 
                          d="M5 11 L12 11 L12 20 Z" 
                          fill={`url(#tierGrad-${idx})`}
                          stroke={tier.color}
                          strokeWidth="1"
                          filter={`url(#tierGlow-${idx})`}
                        />
                        <path 
                          d="M19 11 L12 11 L12 20 Z" 
                          fill={tier.color}
                          stroke={tier.color}
                          strokeWidth="1"
                          opacity="0.7"
                          filter={`url(#tierGlow-${idx})`}
                        />
                        {/* Culet (bottom point) highlight */}
                        <circle 
                          cx="12" 
                          cy="20" 
                          r="0.5" 
                          fill="white" 
                          opacity="0.6"
                        />
                        {/* Facet lines for depth */}
                        <path 
                          d="M8 9 L12 11 M16 9 L12 11 M12 4 L12 11" 
                          stroke={tier.color}
                          strokeWidth="0.5"
                          opacity="0.3"
                          fill="none"
                        />
                      </>
                    )}
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{
                    fontSize: '18px',
                    color: '#F4E7C3',
                    fontWeight: 'bold',
                    marginBottom: '4px',
                    fontFamily: 'Cinzel, serif'
                  }}>
                    {tier.name}
                  </h4>
                  <p style={{
                    fontSize: '16px',
                    color: '#D4AF37',
                    fontWeight: 'bold'
                  }}>
                    {tier.range}
                  </p>
                </div>
              </div>
              <p style={{ color: '#F4E7C3', fontSize: '13px', lineHeight: '1.5' }}>
                {tier.description}
              </p>
            </div>
          ))}
        </div>

        {/* Verification Requirements */}
        <div className="glass-panel" style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontSize: '18px',
            color: '#D4AF37',
            fontFamily: 'Cinzel, serif',
            marginBottom: '16px'
          }}>
            Verification Requirements
          </h3>
          <ul style={{ color: '#F4E7C3', fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px' }}>
            <li>Recent tax returns or W-2 forms</li>
            <li>Bank statements (last 3 months)</li>
            <li>Investment account statements</li>
            <li>Business ownership documentation (if applicable)</li>
            <li>Government-issued photo ID</li>
          </ul>
          <p style={{ color: 'rgba(244, 231, 195, 0.7)', fontSize: '12px', marginTop: '16px', lineHeight: '1.6' }}>
            All documents are securely encrypted and reviewed by our verification team. Your privacy is guaranteed.
          </p>
        </div>

        {/* Action Button */}
        {verificationStatus !== 'verified' && (
          <button
            className="btn-primary"
            style={{ width: '100%', marginTop: '8px' }}
            onClick={() => alert('Document upload feature will be implemented')}
          >
            <Upload size={20} style={{ marginRight: '8px' }} />
            {verificationStatus === 'pending' ? 'Upload Additional Documents' : 'Start Verification Process'}
          </button>
        )}
      </div>
    </div>
  );
};

export default IncomeVerificationPage;
